import React, { useEffect, useState } from "react";
import { supabase } from "../supabase";
import { getAuthRedirectUrl } from "../helpers/authHelpers";

const VARIANT_COPY = {
  signup: {
    title: "Check your email",
    body: "We sent a confirmation link to the address below. Verify your email before signing in.",
  },
  duplicate: {
    title: "Check your email",
    body: "If you already have an account, sign in. Otherwise, check your inbox for a confirmation link we may have sent earlier.",
  },
  unverified: {
    title: "Verify your email",
    body: "Your email isn't verified yet. Check your inbox or resend the confirmation email below.",
  },
};

const RESEND_COOLDOWN_SEC = 60;

function MailIcon() {
  return (
    <svg
      viewBox="0 0 52 52"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect
        x="6"
        y="12"
        width="40"
        height="28"
        rx="4"
        stroke="#c9a84c"
        strokeWidth="2"
        fill="none"
      />
      <path
        d="M6 16l20 14 20-14"
        stroke="#c9a84c"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function EmailConfirmationScreen({ email, variant = "signup", onBackToSignIn }) {
  const copy = VARIANT_COPY[variant] || VARIANT_COPY.signup;

  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;

    const timer = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) return 0;
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleResend = async () => {
    if (!email || isResending || resendCooldown > 0) return;

    setIsResending(true);
    setResendSuccess(false);
    setResendError(null);

    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email,
        options: {
          emailRedirectTo: getAuthRedirectUrl(),
        },
      });

      if (error) throw error;

      setResendSuccess(true);
      setResendCooldown(RESEND_COOLDOWN_SEC);
    } catch (err) {
      console.error("RESEND CONFIRMATION ERROR:", err);
      setResendError(err.message || "Failed to resend confirmation email.");
    } finally {
      setIsResending(false);
    }
  };

  const resendDisabled = isResending || resendCooldown > 0 || !email;
  const resendLabel = isResending
    ? "Sending…"
    : resendCooldown > 0
      ? `Resend available in ${resendCooldown}s`
      : "Resend Confirmation Email";

  return (
    <div className="screen nx-screen ci-success-screen">
      <div className="ci-success-content">
        <div className="ci-success-icon">
          <MailIcon />
        </div>

        <h1 className="ci-success-title">{copy.title}</h1>

        <p className="ci-success-sub">{copy.body}</p>

        {email && (
          <p
            style={{
              color: "#c9a84c",
              fontWeight: 600,
              marginBottom: "4px",
            }}
          >
            {email}
          </p>
        )}

        <p className="ci-success-sub" style={{ marginTop: 0 }}>
          Didn&apos;t receive it? Check your spam folder.
        </p>

        {resendSuccess && (
          <p className="nx-form-success">
            Confirmation email sent. It may take a minute to arrive.
          </p>
        )}

        {resendError && <p className="nx-form-error">{resendError}</p>}

        <button
          type="button"
          className="nx-btn nx-btn-gold"
          style={{ marginTop: "8px", width: "100%", maxWidth: "320px" }}
          onClick={handleResend}
          disabled={resendDisabled}
        >
          {resendLabel}
        </button>

        <button
          type="button"
          className="nx-btn nx-btn-ghost"
          style={{ marginTop: "4px", width: "100%", maxWidth: "320px" }}
          onClick={onBackToSignIn}
        >
          Back to Sign In
        </button>
      </div>
    </div>
  );
}
